import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { AlertTriangle, ShieldAlert, Save } from "lucide-react";
import { AiRoutingFormSections } from "./routing/AiRoutingFormSections";
import {
  JOB_ROWS,
  type JobKind,
  type ModelOverrides,
  type ProviderAxis,
  type RoutingConfig,
} from "./routing/aiRoutingTypes";

const numberField = (v: unknown, fallback: number): number =>
  typeof v === "number" && Number.isFinite(v) ? v : fallback;

export const AiRoutingTab = () => {
  const [config, setConfig] = useState<RoutingConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    const { data, error } = await supabase.functions.invoke("admin-ai-routing", {
      body: { action: "get" },
    });
    if (error) {
      setError(error.message ?? "Failed to load AI routing config");
    } else if (data?.config) {
      setConfig(data.config as RoutingConfig);
    }
    setLoading(false);
  };

  useEffect(() => {
    void load();
  }, []);

  const save = async () => {
    if (!config) return;
    setSaving(true);
    const cleaned: ModelOverrides = {};
    for (const row of JOB_ROWS) {
      const cell = config.model_overrides?.[row.key] ?? {};
      const lov = (cell.lovable ?? "").toString().trim();
      const orr = (cell.openrouter ?? "").toString().trim();
      if (lov || orr) {
        cleaned[row.key] = {
          ...(lov ? { lovable: lov } : {}),
          ...(orr ? { openrouter: orr } : {}),
        };
      }
    }
    const { data, error } = await supabase.functions.invoke("admin-ai-routing", {
      body: {
        action: "update",
        openrouter_admin_enabled: config.openrouter_admin_enabled,
        cap_starter_monthly_usd: numberField(config.cap_starter_monthly_usd, 0),
        cap_plus_monthly_usd: numberField(config.cap_plus_monthly_usd, 0),
        cap_pro_monthly_usd: numberField(config.cap_pro_monthly_usd, 0),
        monthly_admin_budget_total_usd: numberField(config.monthly_admin_budget_total_usd, 0),
        disable_premium_writing_for_plus: config.disable_premium_writing_for_plus,
        disable_premium_writing_for_pro: config.disable_premium_writing_for_pro,
        model_overrides: cleaned,
      },
    });
    setSaving(false);
    if (error) {
      toast.error(error.message ?? "Failed to save AI routing config");
      return;
    }
    if (data?.config) setConfig(data.config as RoutingConfig);
    toast.success("AI routing settings saved");
  };

  const set = <K extends keyof RoutingConfig>(key: K, value: RoutingConfig[K]) => {
    setConfig((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const setModelCell = (job: JobKind, axis: ProviderAxis, value: string) => {
    setConfig((prev) => {
      if (!prev) return prev;
      const next: ModelOverrides = { ...(prev.model_overrides ?? {}) };
      const cell = { ...(next[job] ?? {}) };
      cell[axis] = value;
      next[job] = cell;
      return { ...prev, model_overrides: next };
    });
  };

  return (
    <div className="space-y-4">
      <Card className="shadow-soft">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-primary" />
            AI paths & admin-funded OpenRouter pool (Rail 3)
          </CardTitle>
          <CardDescription>
            Three independent AI paths — <strong>AI Chat</strong>, <strong>Planning Agent</strong>,
            and <strong>Writing Agent</strong> — each with its own default model on Excerptus AI
            and OpenRouter. Below those, the admin OpenRouter pool acts as a system-wide
            failover for all three paths when a workspace has no BYOK key.
            <strong className="text-foreground"> Off by default.</strong> Engage during
            incidents only. Per-tier USD caps + the global monthly budget act as
            guardrails. This switch never overrides the BYOK requirement on Elite or Agency.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {loading && (
            <div className="space-y-3">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          )}

          {error && !loading && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Couldn't load AI routing config</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {!loading && config && (
            <>
              <AiRoutingFormSections config={config} set={set} setModelCell={setModelCell} />

              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>OpenRouter API key</AlertTitle>
                <AlertDescription>
                  Rail 3 requires the <code>OPENROUTER_API_KEY</code> secret to be set on the
                  edge runtime. If it's missing, the master switch stays moot — the resolver
                  silently falls through to credits. Add or rotate the secret in Lovable Cloud
                  settings, not here.
                </AlertDescription>
              </Alert>

              <div className="flex items-center justify-between gap-4 border-t pt-4">
                <p className="text-xs text-muted-foreground">
                  {config.updated_at
                    ? `Last updated ${new Date(config.updated_at).toLocaleString()}`
                    : "No recorded updates yet"}
                </p>
                <Button onClick={save} disabled={saving}>
                  <Save className="mr-2 h-4 w-4" />
                  {saving ? "Saving…" : "Save changes"}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
